import { useMutation } from '@tanstack/react-query'
import type { SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'

import { resetPassword } from './api'

const MIN_PASSWORD_LENGTH = 12

interface FieldErrors {
  password?: string
  confirmPassword?: string
}

export interface ResetPasswordPageProps {
  readonly uid: string
  readonly token: string
  readonly onNavigateToLogin: () => void
  readonly onRequestNewLink: () => void
}

export function ResetPasswordPage({
  uid,
  token,
  onNavigateToLogin,
  onRequestNewLink,
}: ResetPasswordPageProps) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const mutation = useMutation({
    mutationFn: resetPassword,
  })

  const linkIsMissing = uid.trim() === '' || token.trim() === ''

  function validate(): FieldErrors {
    const errors: FieldErrors = {}

    if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = t('auth.reset.errors.passwordLength')
    }

    if (confirmPassword !== password) {
      errors.confirmPassword = t('auth.reset.errors.passwordMismatch')
    }

    return errors
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    const errors = validate()
    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    mutation.mutate({ userId: uid, token, newPassword: password })
  }

  const apiMessages = describeApiError(mutation.error)
  const [firstMessage, ...restMessages] = apiMessages

  return (
    <main className="flex min-h-full items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>{t('auth.reset.title')}</CardTitle>
            <CardDescription>{t('auth.reset.subtitle')}</CardDescription>
          </CardHeader>

          {linkIsMissing ? (
            <CardContent className="flex flex-col gap-4">
              <Alert variant="danger" title={t('auth.reset.invalidLinkTitle')}>
                {t('auth.reset.invalidLinkDescription')}
              </Alert>
              <Button className="w-full" onClick={onRequestNewLink}>
                {t('auth.reset.requestNewLink')}
              </Button>
            </CardContent>
          ) : mutation.isSuccess ? (
            <CardContent className="flex flex-col gap-4">
              <Alert variant="success" title={t('auth.reset.successTitle')}>
                {t('auth.reset.successDescription')}
              </Alert>
              <Button className="w-full" onClick={onNavigateToLogin}>
                {t('auth.reset.goToLogin')}
              </Button>
            </CardContent>
          ) : (
            <form
              onSubmit={(event) => {
                handleSubmit(event)
              }}
              noValidate
            >
              <CardContent className="flex flex-col gap-4">
                {firstMessage !== undefined ? (
                  <Alert variant="danger" title={firstMessage}>
                    {restMessages.length > 0 ? (
                      <ul className="list-disc pl-4">
                        {restMessages.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    ) : null}
                  </Alert>
                ) : null}
                <Input
                  id="reset-password"
                  type="password"
                  label={t('auth.reset.passwordLabel')}
                  hint={t('auth.reset.passwordHint')}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value)
                  }}
                  error={fieldErrors.password}
                />
                <Input
                  id="reset-confirm-password"
                  type="password"
                  label={t('auth.reset.confirmPasswordLabel')}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value)
                  }}
                  error={fieldErrors.confirmPassword}
                />
              </CardContent>
              <CardFooter className="flex-col items-stretch gap-3">
                <Button type="submit" loading={mutation.isPending} className="w-full">
                  {mutation.isPending ? t('auth.reset.submitting') : t('auth.reset.submit')}
                </Button>
                <button
                  type="button"
                  onClick={onNavigateToLogin}
                  className="text-center text-sm font-medium text-action-primary hover:underline"
                >
                  {t('auth.reset.goToLogin')}
                </button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </main>
  )
}
